// "How to avoid phishing emails" guide content. Update every ~4 months.

export const GUIDE_HERO = {
  eyebrow: "Prevention Guide",
  title: "How to avoid phishing emails & scams",
  lede: "Phishing is the most common way attackers get into an organisation \u2014 and it works by fooling people, not computers. This guide walks the whole team through how to recognise a scam, what to do about it, and the exact steps to take whether or not you clicked. Take a few minutes here and you\u2019ll spot almost every attack.",
  image: "https://images.unsplash.com/photo-1740479049014-274a2c756317?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxwaGlzaGluZyUyMGVtYWlsJTIwc2NhbSUyMGFsZXJ0JTIwd2FybmluZ3xlbnwwfHx8fDE3ODc5NTc2MzF8MA&ixlib=rb-4.1.0&q=85",
};

// The 6-point quick check you run on EVERY email.
export const QUICK_CHECK = [
  { icon: "At", title: "Check the real sender", text: "Look at the full email address, not just the display name. \u201CIT Support\u201D can hide any address. Watch for lookalike domains (mcsreginaa.com, micr0soft.com)." },
  { icon: "LinkSimple", title: "Hover before you click", text: "Rest your mouse over any link (long-press on mobile) to see where it truly goes. If it doesn\u2019t match the company\u2019s real website, don\u2019t click." },
  { icon: "Clock", title: "Beware urgency", text: "\u201CAct now,\u201D \u201Cwithin 24 hours,\u201D \u201Caccount will be closed.\u201D Pressure is designed to stop you thinking. Slow down." },
  { icon: "Key", title: "Never share credentials", text: "No real service emails you a link to \u2018confirm\u2019 your password, PIN, or card number. Treat any such request as an attack." },
  { icon: "TextAa", title: "Spot odd wording", text: "Generic greetings (\u201CDear User\u201D), spelling mistakes, and awkward grammar are common signs \u2014 though good scams can look polished too." },
  { icon: "Paperclip", title: "Distrust surprise attachments", text: "Unexpected invoices, \u2018voicemails,\u2019 or .zip/.html files can carry malware. Confirm with the sender before opening." },
];

// Every common situation, each with a worked example email and the red flags in it.
export const SITUATIONS = [
  {
    id: "s1",
    title: "The lookalike sender",
    summary: "The name looks familiar, but the actual address is subtly wrong.",
    email: {
      sender: "Microsoft 365 <account-security@micros0ft-support.com>",
      subject: "Action required: verify your account",
      body: "Dear User,\n\nWe were unable to verify your Microsoft 365 account. Please confirm your details to keep your access active.\n\nVerify account: http://micros0ft-support.com/login",
      links: ["http://micros0ft-support.com/login"],
    },
    flags: [
      "The domain is \u2018micros0ft-support.com\u2019 \u2014 a zero instead of an \u2018o\u2019, and not a real Microsoft address.",
      "Generic \u201CDear User\u201D greeting instead of your name.",
      "Asks you to \u2018confirm details\u2019 through a link.",
    ],
  },
  {
    id: "s2",
    title: "Urgency & threats",
    summary: "Fear and time pressure push you to act before you think.",
    email: {
      sender: "HR Department <hr@mcsreginaa-portal.com>",
      subject: "FINAL NOTICE: Payroll suspended in 12 hours",
      body: "Your payroll profile will be SUSPENDED in 12 hours due to missing information. Update immediately to avoid losing this month\u2019s pay.\n\nUpdate now: http://mcsreginaa-portal.com/payroll",
      links: ["http://mcsreginaa-portal.com/payroll"],
    },
    flags: [
      "Extreme urgency and a threat (losing your pay).",
      "Misspelled lookalike domain \u2018mcsreginaa-portal.com\u2019.",
      "Pressures you to click a link rather than use the normal portal you already know.",
    ],
  },
  {
    id: "s3",
    title: "The CEO / manager request",
    summary: "Someone \u2018senior\u2019 asks for money, gift cards, or confidential data.",
    email: {
      sender: "Director <director.mcs@gmail.com>",
      subject: "Quick favour \u2014 are you at your desk?",
      body: "Hi, I\u2019m going into meetings and can\u2019t call. I need you to purchase four $200 gift cards for a client and send me the codes. I\u2019ll reimburse you today. Keep this between us for now.\n\nThanks",
      links: [],
    },
    flags: [
      "Uses a personal Gmail address, not the director\u2019s real work email.",
      "Requests gift cards \u2014 a hallmark of Business Email Compromise.",
      "Asks for secrecy and can\u2019t be reached by phone \u2014 to stop you verifying.",
    ],
  },
  {
    id: "s4",
    title: "Credential harvesting",
    summary: "A fake login page tries to capture your username and password.",
    email: {
      sender: "IT Helpdesk <no-reply@secure-mailupdate.net>",
      subject: "Your mailbox is almost full \u2014 re-validate now",
      body: "You have used 99% of your storage. Re-validate your email to continue sending and receiving messages.\n\nRe-validate: http://secure-mailupdate.net/owa",
      links: ["http://secure-mailupdate.net/owa"],
    },
    flags: [
      "Link goes to an unrelated domain, not your organisation.",
      "Manufactured problem (\u2018mailbox full\u2019) to make you act.",
      "\u2018Re-validate\u2019 pages exist only to steal what you type.",
    ],
  },
  {
    id: "s5",
    title: "Too good to be true",
    summary: "Prizes, refunds, or bonuses you never signed up for.",
    email: {
      sender: "Rewards Team <claims@amaz0n-rewards.info>",
      subject: "\ud83c\udf89 You\u2019ve won a $500 gift card!",
      body: "Congratulations! You have been selected to receive a $500 reward. Claim within 24 hours by confirming your address and card details.\n\nClaim your prize: http://amaz0n-rewards.info/claim",
      links: ["http://amaz0n-rewards.info/claim"],
    },
    flags: [
      "You didn\u2019t enter any contest \u2014 unexpected \u2018winnings\u2019 are bait.",
      "\u2018Amaz0n\u2019 with a zero and a fake .info domain.",
      "Asks for card details to \u2018claim\u2019 \u2014 real prizes never do this.",
    ],
  },
  {
    id: "s6",
    title: "Malicious attachment",
    summary: "A surprise file that carries a virus or a hidden login trap.",
    email: {
      sender: "Accounts <billing@invoice-delivery.co>",
      subject: "Invoice #INV-90421 attached \u2014 payment overdue",
      body: "Please find your overdue invoice attached. Open the document and enable content to view the payment details.\n\n[ Invoice_90421.html ]",
      links: [],
    },
    flags: [
      "An invoice you weren\u2019t expecting, from an unknown sender.",
      "\u2018Enable content\u2019 or opening an .html/.zip file can install malware.",
      "Pressure word \u2018overdue\u2019 to make you open it quickly.",
    ],
  },
];

export const DO_THIS = [
  "Slow down and read the email fully before doing anything.",
  "Verify unusual requests through a channel you already trust \u2014 call or message the person directly.",
  "Hover over links and check the real web address first.",
  "When something feels off, report it to your administrator and delete it.",
  "Keep your device and browser updated, and lock your screen when you step away.",
];

export const DONT_DO = [
  "Don\u2019t click links or open attachments you weren\u2019t expecting.",
  "Don\u2019t enter your password or personal details from an email link.",
  "Don\u2019t reply to or forward the suspicious email to coworkers.",
  "Don\u2019t buy gift cards or move money based only on an email.",
  "Don\u2019t ignore it \u2014 reporting helps protect the whole team.",
];

// The two paths after an incident.
export const IF_CLICKED = {
  image: "https://images.unsplash.com/photo-1698047681465-a75bcf38379e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxwZXJzb24lMjB3b3JyaWVkJTIwbGFwdG9wJTIwb2ZmaWNlJTIwc2VjdXJpdHl8ZW58MHx8fHwxNzg3OTU3NjMxfDA&ixlib=rb-4.1.0&q=85",
  intro: "Don\u2019t panic \u2014 acting fast limits the damage. Example: you clicked a \u2018mailbox full\u2019 link and typed your password on the page.",
  steps: [
    { title: "Disconnect if you downloaded anything", text: "If a file downloaded or ran, disconnect the device from Wi-Fi/network to stop it spreading." },
    { title: "Change your password immediately", text: "From a different, trusted device, change the password you entered \u2014 and any other account that used the same password." },
    { title: "Report it right away", text: "Contact your administrator / IT so they can watch for suspicious activity, reset access, and warn others." },
    { title: "Turn on extra protection", text: "Enable multi-factor authentication where available so a stolen password alone isn\u2019t enough." },
    { title: "Watch for follow-up scams", text: "Attackers often follow up. Be extra cautious of new \u2018support\u2019 messages about the incident." },
  ],
};

export const IF_NOT_CLICKED = {
  image: "https://images.unsplash.com/photo-1633265486064-086b219458ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjeWJlciUyMHNlY3VyaXR5JTIwcGFzc3dvcmQlMjBwcm90ZWN0aW9uJTIwbG9ja3xlbnwwfHx8fDE3ODc5NTc2MzF8MA&ixlib=rb-4.1.0&q=85",
  intro: "You spotted it \u2014 nice work. A few quick steps keep everyone safe. Example: you received the \u2018CEO gift card\u2019 email and did not reply.",
  steps: [
    { title: "Do not interact", text: "Don\u2019t click, reply, or open attachments \u2014 even to \u2018tell them off.\u2019 Any response confirms your address is active." },
    { title: "Report it", text: "Forward it to your administrator / IT (or use your report button) so they can block the sender and alert the team." },
    { title: "Delete the email", text: "After reporting, delete it so you don\u2019t click it later by accident." },
    { title: "Mark as junk / phishing", text: "Use your mail app\u2019s \u2018Report phishing\u2019 or \u2018Junk\u2019 option to train filters." },
    { title: "Stay alert", text: "If it impersonated a coworker, give them a heads-up through a trusted channel \u2014 they may be targeted too." },
  ],
};

export const REPORT_CONTACT = {
  note: "When in doubt, always ask first \u2014 it\u2019s never a bother.",
  org: "Mobile Crisis Services Regina",
  phone: "+1.306.570.9865",
  email: "Ben.Tanfous-Foued@mcsregina.ca",
};
